import { ElementRef } from '@angular/core';
import {
  CellClickedEvent,
  CellValueChangedEvent,
  ICellRendererParams,
  RowClassParams,
} from 'ag-grid-enterprise';
import { CONSTANTS_DETAILS } from './constant/constants';

// date formatter utility function for ag-grid date filtering.
// This function compares the filter date with the cell value date.
export const gridDateFormatter = {
  comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
    const filteredDate = new Date(cellValue).toLocaleDateString();
    const cellDate = new Date(filteredDate);
    if (cellDate < filterLocalDateAtMidnight) {
      return -1;
    } else if (cellDate > filterLocalDateAtMidnight) {
      return 1;
    } else {
      return 0;
    }
  },
  browserDatePicker: true,
  inRangeInclusive: true,
};
// This utility function determines the filter type based on the cell value.
export const dynamicFilterTypeApply = {
  switch: (cellValue?: string | number | boolean | Date | null | undefined) => {
    if (cellValue && !isNaN(Number(cellValue))) {
      return 'agNumberColumnFilter';
    }
    if (cellValue && Date.parse(cellValue as string)) {
      return 'agDateColumnFilter';
    }
    return 'agTextColumnFilter';
  },
};
// This utility function renders action buttons in the table grid.
// It checks if the row is editable and returns appropriate action buttons.
export const tableActionCellRenderer = (
  params?: CellClickedEvent | ICellRendererParams,
  titles?: {
    save?: string;
    cancel?: string;
    edit?: string;
    history?: string;
    view?: string;
    send?: string;
  },
  isHistory?: boolean,
  openDialog?: boolean,
  isFormulator?: boolean,
) => {
  if (params?.data?.isEditable) {
    return `<span title="${titles?.save}" class="material-symbols-outlined table-actions table-actions-save" id="save">check</span> 
          &nbsp;
          <span title="${titles?.cancel}" class="material-symbols-outlined table-actions table-actions-close" id="cancel">close</span>`;
  } else if (isHistory) {
    return `<span title="${titles?.edit}" class="material-symbols-outlined table-actions" id="edit">edit</span>
          &nbsp;
          <span title="${titles?.history}" class="material-symbols-outlined table-actions" id="history">history</span>`;
  } else if (openDialog) {
    return `<span title="${titles?.view}" class="material-symbols-outlined table-actions-open" id="open">visibility</span>`;
  } else {
    if (isFormulator) {
      return `<span title="${titles?.send}" class="material-symbols-outlined table-actions" id="send">send</span>`;
    }
    return `<span title="${titles?.edit}" class="material-symbols-outlined table-actions" id="edit">edit</span>`;
  }
};
export const escapeHtml = (text: string): string => {
  const map: Record<'&' | '<' | '>' | '"' | "'", string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };
  return text.replace(/[&<>"']/g, (m) => map[m as keyof typeof map]);
};
//This utility function updates the icons of a switch component based on the provided currency symbols from component level.
export const updateSwitchIcons = (
  toggleRef: ElementRef,
  dollar: string,
  nutriProfile?: boolean,
): void => {
  if (toggleRef) {
    const nativeElem = toggleRef.nativeElement as HTMLElement;
    const iconContainer =
      nativeElem.querySelector('.mdc-switch__icons') || nativeElem;
    nativeElem.querySelector('.mdc-switch__icon--on')?.remove();
    nativeElem.querySelector('.mdc-switch__icon--off')?.remove();
    if (nutriProfile) {
      const percent = document.createElement('span');
      percent.className = 'mdc-switch__icon mdc-switch__icon--on';
      percent.innerHTML = '%';
      const currency = document.createElement('span');
      currency.className = 'mdc-switch__icon mdc-switch__icon--off';
      currency.innerHTML = '123';
      currency.classList.add('small-currency-icon');
      iconContainer.appendChild(percent);
      iconContainer.appendChild(currency);
    } else {
      const percent = document.createElement('span');
      percent.className = 'mdc-switch__icon mdc-switch__icon--off';
      percent.innerHTML = '%';
      const currency = document.createElement('span');
      currency.className = 'mdc-switch__icon mdc-switch__icon--on';
      currency.innerHTML = escapeHtml(dollar);
      if (dollar.length > 1) {
        currency.classList.add('small-currency-icon');
      }
      iconContainer.appendChild(percent);
      iconContainer.appendChild(currency);
    }
  }
};
//This utility function dynamically sets the currency symbol based on the currency code.
export function getCurrencySymbol(currencyCode: string): string {
  try {
    const formatter = new Intl.NumberFormat('en', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    const formatted = formatter.format(1);
    return formatted.replace(/\d/g, '').trim();
  } catch {
    return currencyCode;
  }
}
export const gridCellValueEdit = (
  params: CellClickedEvent | CellValueChangedEvent,
  editedColumns: string,
) => {
  if (params) {
    params.data.isEditable = true;
    params.api.setGridOption('singleClickEdit', true);
    params.api.startEditingCell({
      rowIndex: params.rowIndex || 0,
      colKey: editedColumns,
    });
    params.api.setGridOption('rowClassRules', {
      'new-row': (param: RowClassParams) => {
        if (
          param?.data &&
          !Object.keys(param?.data as object).includes('isEditable')
        ) {
          param.data.isEditable = false;
        }
        return param?.data ? (param?.data?.isEditable as boolean) : false;
      },
    });
  }
};
export const gridCellDefaultView = (
  params: CellClickedEvent | CellValueChangedEvent,
  refreshedColumns: string[] = [],
  defaultUndoCount: number = 0,
) => {
  if (params) {
    params.data.isEditable = false;
    params.api.clearFocusedCell();
    params.api.stopEditing();
    params.api.setGridOption('rowClassRules', {
      'no-new-row': (param: RowClassParams) => {
        if (
          (param?.data as object) &&
          !Object.keys(param?.data as object).includes('isEditable')
        ) {
          param.data.isEditable = false;
        }
        return (
          ((param?.data && !(param?.data?.isEditable as boolean)) as boolean) ||
          false
        );
      },
    });
    gridStopEditAndResetValue(params, refreshedColumns, defaultUndoCount);
  }
};

export const gridStopEditAndResetValue = (
  params: CellClickedEvent | CellValueChangedEvent,
  refreshedColumns: string[] = [],
  defaultUndoCount: number,
) => {
  if (params) {
    Array.from({
      length: params.data.cellValueChangeCount || defaultUndoCount,
    }).forEach(() => {
      params.api.undoCellEditing();
    });
    params.data.cellValueChangeCount = 0;
    gridRefreshCells(params, refreshedColumns);
  }
};

export const gridRefreshCells = (
  params: CellClickedEvent | CellValueChangedEvent,
  refreshedColumns: string[] = [],
) => {
  if (params) {
    params.api.refreshCells({
      rowNodes: [params.node],
      force: true,
      columns: refreshedColumns,
    });
  }
};

export const gridSetFocusCell = (
  params: CellClickedEvent | CellValueChangedEvent,
  lastEditedRowIndex: number | null,
  colKey: string,
) => {
  if (params.data.isEditable) {
    params.api.setFocusedCell(params.rowIndex || 0, colKey);
    params.api.startEditingCell({
      rowIndex: params.rowIndex || 0,
      colKey: colKey,
    });
  } else {
    params.api.setFocusedCell(lastEditedRowIndex || 0, colKey);
  }
};
export const numberValidation = (val: string): boolean => {
  const regex: RegExp = /^\d{0,3}(\.\d{0,4})?$/;
  return regex.test(val);
};
export const aspValidation = (val: string): boolean => {
  const regex: RegExp = /^\d+(\.\d{0,3})?$/;
  return regex.test(val);
};

export const convertToDecimel = (val: string): string => {
  if (val === null || val === undefined || val.trim() === '') {
    return '-';
  }
  const num = Number(val);
  if (!isNaN(num)) {
    return num.toFixed(6).toString();
  }
  return val;
};

export function formatASP(value: number | string): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  const safeNum = isNaN(num) ? 0 : num;
  return safeNum.toFixed(CONSTANTS_DETAILS.ASP_DECIMAL_PLACES);
}
